import React from 'react';
import { Link } from '@inertiajs/react';
import AdminLayout from '../../layouts/AdminLayout';
import DataTable, { type Column } from '../../components/admin/DataTable';
import { day, since } from '../../components/admin/format';

type Row = {
  id: number;
  token: string;
  label: string;
  owner: string | null;
  created_at: string;
  used_at: string | null;
  state: 'ok' | 'revoked' | 'expired' | 'warn';
  note: string | null;
};

type CredentialsProps = { kind: string; rows: Row[]; page: number; per: number; total: number };
type CredentialsComponent = React.FC<CredentialsProps> & {
  layout?: (page: React.ReactNode) => React.ReactNode;
};

const KINDS: [string, string][] = [
  ['api-tokens', 'API Token'],
  ['jwt', 'JWT'],
  ['oauth-apps', 'OAuth 应用'],
  ['oauth-tokens', 'OAuth 授权'],
  ['ai-keys', 'AI Key'],
];

const STATE_PILL: Record<Row['state'], [string, string]> = {
  ok: ['有效', 'bg-admin-okSoft text-admin-ok'],
  revoked: ['已吊销', 'bg-admin-dangerSoft text-admin-danger'],
  expired: ['已过期', 'bg-admin-surface2 text-admin-muted'],
  warn: ['注意', 'bg-admin-warnSoft text-admin-warn'],
};

// 同样三个 state 值，在 ai-keys 上说的是别的事：没有「吊销 / 过期」这两个
// 概念，有的是「上次调用失败」和「配了但从未用过」。复用 state 枚举而不是
// 新加两个值 —— 那会让另外四类也多出两个用不上的分支。
const AI_STATE_PILL: Partial<Record<Row['state'], [string, string]>> = {
  ok: ['可用', 'bg-admin-okSoft text-admin-ok'],
  revoked: ['上次失败', 'bg-admin-dangerSoft text-admin-danger'],
  expired: ['从未用过', 'bg-admin-surface2 text-admin-muted'],
};

// 四种 kind 的第三、四列含义不同，表头随之变。
const HEADERS: Record<string, { token: string; label: string; owner: string; used: string }> = {
  'api-tokens': { token: '令牌', label: '名称', owner: '归属', used: '最近使用' },
  jwt: { token: '令牌', label: '类型', owner: '归属', used: '过期于' },
  'oauth-apps': { token: 'uid', label: '应用名', owner: 'redirect_uri', used: '—' },
  'oauth-tokens': { token: '令牌', label: '应用', owner: '资源所有者', used: '—' },
  // 「备注」那一列在 ai-keys 上装的是 api_base（兼容口）或默认标记 ——
  // 一把 key 把数据发去哪台机器，是这张表唯一比其他四类多出来的信息。
  'ai-keys': { token: 'Key', label: '供应商', owner: '归属', used: '最近使用' },
};

function columnsFor(kind: string): Column<Row>[] {
  const h = HEADERS[kind];
  const cols: Column<Row>[] = [
    { key: 'token', header: h.token, width: '196px', mono: true, render: (r) => r.token },
    { key: 'label', header: h.label, width: '170px', truncate: true, render: (r) => r.label },
    {
      key: 'owner',
      header: h.owner,
      truncate: true,
      render: (r) => r.owner ?? <span className="text-admin-muted">—</span>,
    },
    { key: 'created', header: '创建', width: '96px', render: (r) => day(r.created_at) },
  ];

  if (h.used !== '—') {
    cols.push({
      key: 'used',
      header: h.used,
      width: '104px',
      render: (r) => (
        <span className="text-admin-muted">
          {r.used_at ? (kind === 'jwt' ? day(r.used_at) : since(r.used_at)) : '从未'}
        </span>
      ),
    });
  }

  cols.push({
    key: 'note',
    header: '备注',
    width: '190px',
    truncate: true,
    render: (r) => <span className="text-admin-muted">{r.note ?? '—'}</span>,
  });

  cols.push({
    key: 'state',
    header: '状态',
    width: '78px',
    render: (r) => {
      const [label, cls] =
        (kind === 'ai-keys' ? AI_STATE_PILL[r.state] : undefined) ?? STATE_PILL[r.state];
      return <span className={`rounded px-1.5 py-px text-[11px] font-semibold ${cls}`}>{label}</span>;
    },
  });

  return cols;
}

const Credentials: CredentialsComponent = ({ kind, rows, page, per, total }) => (
  <>
    <h1 className="mb-3 text-[14.5px] font-semibold tracking-tight">
      <span className="font-normal text-admin-muted">凭证与会话 / </span>
      {KINDS.find(([k]) => k === kind)?.[1]}
    </h1>

    <div className="mb-3 flex gap-0.5 border-b border-admin-line">
      {KINDS.map(([k, label]) => (
        <Link
          key={k}
          href={`/admin/credentials/${k}`}
          className={`-mb-px border-b-2 px-2.5 py-1.5 text-[12.5px] ${
            k === kind
              ? 'border-admin-accent font-semibold text-admin-accent'
              : 'border-transparent text-admin-ink2 hover:text-admin-ink'
          }`}
        >
          {label}
        </Link>
      ))}
    </div>

    <DataTable
      columns={columnsFor(kind)}
      rows={rows}
      rowKey={(r) => r.id}
      empty="没有这类凭证"
      pagination={{ page, per, total }}
    />

    <div className="mt-3 max-w-[94ch] rounded-md border border-dashed border-admin-line2 bg-admin-surface2 px-3 py-2 text-[12px] leading-relaxed text-admin-muted">
      <b className="font-semibold text-admin-ink2">令牌值一律截断，永不完整渲染。</b>
      展示的是前 8 位 + 后 4 位；完整值连 admin 也看不到 —— 它在库里只有校验用途，
      渲染到页面等于多造一个泄露点（浏览器历史、截图、屏幕共享）。
      <b className="font-semibold text-admin-ink2">吊销按钮不在本期</b>：
      写操作与配套的审计表要一起落地。
    </div>
  </>
);

Credentials.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>;

export default Credentials;
